/**
 * Inspection Builder - New Implementation
 *
 * Main entry point for the Inspection Builder module.
 * Simplified without language dictionary - uses direct field values from GraphQL.
 */
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Box,
	Tab,
	Tabs,
	Button,
	Typography,
	CircularProgress,
	Alert,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Paper,
} from "@mui/material";
import {
	Save as SaveIcon,
	Publish as PublishIcon,
	Undo as UndoIcon,
} from "@mui/icons-material";
import {
	loadConfig,
	saveConfig,
	deployConfig,
	discardPendingConfig,
	setActiveTab,
} from "./redux/slice";
import { detectConfigChanges, getChangeSummary } from "./utils/changeDetector";
import { validateConfiguration } from "./utils/configValidation";
import AssetLayoutsTab from "./views/assetLayoutsTab.jsx";
import InspectionTypesTab from "./views/inspectionTypesTab.jsx";
import ChangesSummary from "./components/changesSummary.jsx";
import ValidationErrorDialog from "./components/validationErrorDialog.jsx";
import "./inspectionBuilder.scss";

// Tab panel component
function TabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`inspection-builder-tabpanel-${index}`}
			aria-labelledby={`inspection-builder-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
		</div>
	);
}

/**
 * Main Inspection Builder component
 */
const InspectionBuilder = () => {
	const dispatch = useDispatch();
	const assetLayoutsTabRef = useRef(null);
	const inspectionTypesTabRef = useRef(null);

	// Get state from Redux
	const { config, deployedConfig, originalConfig, loading, saving, deploying, error } = useSelector(
		(state) => state.inspectionBuilderRedux
	);
	const { activeTab, unsavedChanges, workInProgress } = useSelector(
		(state) => state.inspectionBuilderRedux.ui
	);

	// Dialog states
	const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
	const [changesSummaryOpen, setChangesSummaryOpen] = useState(false);
	const [validationErrorDialogOpen, setValidationErrorDialogOpen] = useState(false);
	const [validationErrors, setValidationErrors] = useState([]);

	// Detect changes between current config and deployed config (or originalConfig as fallback)
	// This shows ALL differences between pending and what's live (persists across saves/reloads)
	// If no deployedConfig exists, compare against originalConfig to show session changes
	const comparisonConfig = deployedConfig || originalConfig;

	// Compute changes with error handling - wrapped in useMemo to avoid re-computation
	const { changes, changeSummary, changeDetectionError } = React.useMemo(() => {
		try {
			const detectedChanges = comparisonConfig ? detectConfigChanges(config, comparisonConfig) : [];
			const summary = getChangeSummary(detectedChanges);
			return { changes: detectedChanges, changeSummary: summary, changeDetectionError: null };
		} catch (err) {
			return {
				changes: [],
				changeSummary: { added: 0, modified: 0, removed: 0, total: 0, byCategory: {} },
				changeDetectionError: err.message
			};
		}
	}, [config, comparisonConfig]);

	// Show changes button if:
	// 1. There are detected changes (comparing config vs deployedConfig/originalConfig), OR
	// 2. workInProgress is true but we can't compute changes (deployedConfig is null)
	// This ensures the indicator persists after reload when there are pending changes
	const showChangesButton = (comparisonConfig && changes.length > 0) || (workInProgress && !deployedConfig);

	// Determine which buttons to show based on unsavedChanges and workInProgress
	// - Show Save when there are unsaved changes (regardless of workInProgress)
	// - Show Deploy when there's a pending config with no unsaved changes
	const showSaveButtons = unsavedChanges;
	const showDeployButtons = workInProgress && !unsavedChanges;

	// Load configuration on mount
	useEffect(() => {
		dispatch(loadConfig());
	}, [dispatch]);

	// Handle tab change
	const handleTabChange = (event, newValue) => {
		dispatch(setActiveTab(newValue));
	};

	// Handle save (save to pending config)
	// Validates configuration first, shows error dialog if invalid (ect2-web pattern)
	const handleSave = () => {
		const validation = validateConfiguration(config);
		if (!validation.isValid) {
			setValidationErrors(validation.errors);
			setValidationErrorDialogOpen(true);
			return;
		}
		dispatch(saveConfig());
	};

	// Handle deploy (publish pending to active)
	// Validates configuration first, shows error dialog if invalid (ect2-web pattern)
	const handleDeploy = () => {
		const validation = validateConfiguration(config);
		if (!validation.isValid) {
			setValidationErrors(validation.errors);
			setValidationErrorDialogOpen(true);
			return;
		}
		dispatch(deployConfig());
	};

	// Handle discard changes
	const handleDiscard = () => {
		dispatch(discardPendingConfig());
		setDiscardDialogOpen(false);
	};

	// Handle navigating to a change from the summary
	const handleNavigateToChange = (change) => {
		setChangesSummaryOpen(false);

		// Navigate based on change category
		if (
			change.category === "Zone Layout" ||
			change.category === "Zone" ||
			change.category === "Component" ||
			change.category === "Components"
		) {
			// Switch to Asset Layouts tab
			dispatch(setActiveTab(0));

			// Find layout index from layoutId in details
			const layoutId = change.details?.layoutId;
			if (layoutId && config?.zoneLayouts) {
				const layoutIndex = config.zoneLayouts.findIndex(
					(layout) => layout.layoutId === layoutId
				);
				if (layoutIndex !== -1) {
					setTimeout(() => {
						assetLayoutsTabRef.current?.scrollToLayout(layoutIndex);
					}, 100);
				}
			}
		} else if (
			change.category === "Inspection Type" ||
			change.category === "Inspection Property" ||
			change.category === "Form Field"
		) {
			// Switch to Inspection Types tab
			dispatch(setActiveTab(1));

			// Find the inspection ID - either directly from details or by matching inspectionName to detailType
			let inspectionId = change.details?.inspectionId;
			if (!inspectionId && change.details?.inspectionName && config?.inspectionDetails) {
				// Find inspection by detailType (inspectionName in change details is the detailType)
				const inspection = config.inspectionDetails.find(
					(insp) => insp.detailType === change.details.inspectionName
				);
				if (inspection) {
					inspectionId = inspection.typeId || inspection.detailType;
				}
			}

			// Scroll to the inspection if we found an ID
			if (inspectionId) {
				setTimeout(() => {
					inspectionTypesTabRef.current?.scrollToInspection(inspectionId);
				}, 100);
			}
		}
	};

	if (loading && !config) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: 400,
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	if (error && !config) {
		return (
			<Box sx={{ p: 3 }}>
				<Alert severity="error">Failed to load configuration: {error}</Alert>
			</Box>
		);
	}

	return (
		<Box className="inspection-builder" sx={{ height: "100%" }}>
			{/* Data format error warning */}
			{changeDetectionError && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					Unable to compare configurations due to a data format issue.
					The configuration may have been saved with an incompatible format.
					Please contact support if this persists.
				</Alert>
			)}

			{/* Header with actions */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
					px: 1,
				}}
			>
				<Box>
					<Typography variant="h5" component="h1" sx={{ mb: 0.5 }}>
						Inspection Builder
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Configure asset layouts and inspection types
					</Typography>
				</Box>

				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{/* Show loading indicator while config is being loaded */}
					{loading && (
						<CircularProgress size={16} sx={{ mr: 1 }} />
					)}

					{/* Show changes button when there are changes */}
					{showChangesButton && (
						<Button
							variant="text"
							size="small"
							onClick={() => setChangesSummaryOpen(true)}
							sx={{ mr: 1 }}
						>
							{changes.length > 0
								? `${changeSummary.total} change${changeSummary.total !== 1 ? "s" : ""}`
								: "Changes pending"}
						</Button>
					)}

					{showSaveButtons && (
						<>
							<Button
								variant="outlined"
								startIcon={<UndoIcon />}
								onClick={() => setDiscardDialogOpen(true)}
								disabled={!unsavedChanges || saving}
							>
								Undo
							</Button>

							<Button
								variant="contained"
								color="primary"
								startIcon={<SaveIcon />}
								onClick={handleSave}
								disabled={!unsavedChanges || saving}
							>
								{saving ? "Saving..." : "Save"}
							</Button>
						</>
					)}

					{showDeployButtons && (
						<>
							<Button
								variant="outlined"
								startIcon={<UndoIcon />}
								onClick={() => setDiscardDialogOpen(true)}
								disabled={deploying}
							>
								Undo
							</Button>

							<Button
								variant="contained"
								color="success"
								startIcon={<PublishIcon />}
								onClick={handleDeploy}
								disabled={deploying}
							>
								{deploying ? "Deploying..." : "Deploy"}
							</Button>
						</>
					)}
				</Box>
			</Box>

			{/* Error banner */}
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{/* Loading indicator for background operations */}
			{loading && config && (
				<Box sx={{ position: "absolute", top: 8, right: 8 }}>
					<CircularProgress size={20} />
				</Box>
			)}

			{/* Tabs */}
			<Paper elevation={1} sx={{ mb: 2 }}>
				<Tabs value={activeTab} onChange={handleTabChange} aria-label="inspection builder tabs">
					<Tab label="Asset Layouts" id="inspection-builder-tab-0" />
					<Tab label="Inspection Types" id="inspection-builder-tab-1" />
				</Tabs>
			</Paper>

			{/* Tab Panels */}
			<TabPanel value={activeTab} index={0}>
				<AssetLayoutsTab ref={assetLayoutsTabRef} />
			</TabPanel>
			<TabPanel value={activeTab} index={1}>
				<InspectionTypesTab ref={inspectionTypesTabRef} />
			</TabPanel>

			{/* Discard Changes Dialog */}
			<Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)}>
				<DialogTitle>Discard Changes?</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to discard all unsaved changes? This action cannot be
						undone.
					</Typography>
					{changeSummary.total > 0 && (
						<Box sx={{ mt: 2 }}>
							<Typography variant="body2" color="text.secondary">
								You will lose {changeSummary.total} change
								{changeSummary.total !== 1 ? "s" : ""}.
							</Typography>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDiscardDialogOpen(false)}>Cancel</Button>
					<Button onClick={handleDiscard} color="error" variant="contained">
						Discard Changes
					</Button>
				</DialogActions>
			</Dialog>

			{/* Changes Summary Dialog */}
			<ChangesSummary
				open={changesSummaryOpen}
				onClose={() => setChangesSummaryOpen(false)}
				changes={changes}
				summary={changeSummary}
				onNavigateToChange={handleNavigateToChange}
			/>

			{/* Validation Error Dialog - shown when save fails validation */}
			<ValidationErrorDialog
				open={validationErrorDialogOpen}
				onClose={() => setValidationErrorDialogOpen(false)}
				errors={validationErrors}
			/>
		</Box>
	);
};

export default InspectionBuilder;
